import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { X, Sparkles } from 'lucide-react';

// Escucha propuestas Classic Plus para el anonId actual.
//
// Estrategia: polling cada 3s mientras la pestaña esté visible + check
// inmediato cuando el usuario vuelve a la pestaña (visibilitychange) o
// vuelve a enfocar la ventana (focus). Sin Firestore SDK extra en el
// bundle. Latencia esperada: 0-3s tras enviar admin.
//
// IMPORTANTE: usamos refs para onRedeem/t porque si los metemos en el
// useEffect dependency array, cada render del padre (Home re-renderiza
// muy a menudo) reinicia el intervalo y polling nunca termina un ciclo.

interface AnonProposal {
  id: string;
  imageUrl: string;
  message: string;
  lang: string;
  price: number;
  nombreNino: string | null;
  classicPlus: boolean;
  proposalType?: 'standard' | 'classicPlus' | 'discount';
  source?: 'draft' | 'live' | null;
}

interface Props {
  onRedeem: (proposal: AnonProposal) => void;
  disabled?: boolean;
}

const POLL_INTERVAL_MS = 3_000;
const SHOWN_KEY = 'funcomunion_proposal_shown';

function getAnonId(): string | null {
  try { return localStorage.getItem('funcomunion_anon_id'); } catch { return null; }
}
function isLoggedIn(): boolean {
  try { return !!localStorage.getItem('funcomunion_user'); } catch { return false; }
}

export function AnonProposalListener({ onRedeem, disabled }: Props) {
  const { t } = useTranslation();

  // Refs para callbacks que no deben re-disparar el useEffect.
  const onRedeemRef = useRef(onRedeem);
  const tRef = useRef(t);
  useEffect(() => { onRedeemRef.current = onRedeem; }, [onRedeem]);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    if (disabled) return;
    if (isLoggedIn()) return;
    const anonId = getAnonId();
    if (!anonId) return;

    let stopped = false;
    let timer: number | null = null;
    let inflight = false; // evita queries solapadas si el servidor tarda

    const showToast = (p: AnonProposal) => {
      // Solo mostrar 1 vez por sesión y por propuesta id
      const shown = sessionStorage.getItem(SHOWN_KEY);
      if (shown === p.id) return;
      sessionStorage.setItem(SHOWN_KEY, p.id);

      // Reportar al backend que ha sido vista
      fetch(`/api/proposal/${p.id}/view`, { method: 'POST' }).catch(() => {});

      const TOAST_ID = `proposal-${p.id}`;
      const tNow = tRef.current;

      const handleRedeem = async () => {
        try { await fetch(`/api/proposal/${p.id}/redeem`, { method: 'POST' }); } catch {}
        toast.dismiss(TOAST_ID);
        onRedeemRef.current(p);
      };
      const handleDismiss = async () => {
        try { await fetch(`/api/proposal/${p.id}/dismiss`, { method: 'POST' }); } catch {}
        toast.dismiss(TOAST_ID);
      };

      toast.custom(
        () => (
          <div
            role="dialog"
            aria-label="Propuesta Classic Plus"
            className="bg-white rounded-2xl shadow-2xl border-2 border-[#D4AF37]/40 overflow-hidden"
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              boxShadow: '0 20px 50px rgba(212,175,55,0.35), 0 4px 20px rgba(0,0,0,0.08)',
            }}
          >
            <div className="flex items-start justify-between px-4 pt-3 pb-2 bg-gradient-to-r from-[#FAF8F5] to-[#FEF3E2]">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#D4AF37]">
                  {p.nombreNino
                    ? `${p.nombreNino} · ${p.proposalType === 'discount' ? 'Propuesta especial' : p.proposalType === 'standard' ? 'Tu figura' : 'Classic Plus'}`
                    : (p.proposalType === 'discount' ? 'Propuesta especial' : p.proposalType === 'standard' ? 'Tu figura' : 'Classic Plus')}
                </span>
              </div>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={tNow('home.proposalToastDismiss')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-4 py-3 flex gap-3">
              <button onClick={handleRedeem} className="flex-shrink-0 group relative" aria-label="Ver propuesta">
                <img
                  src={p.imageUrl}
                  alt="Classic Plus"
                  className="w-24 h-24 object-cover rounded-xl border-2 border-[#D4AF37] group-hover:scale-105 transition-transform"
                />
                <span
                  className="absolute inset-0 rounded-xl ring-2 ring-[#D4AF37]/40 ring-offset-2 ring-offset-white animate-pulse pointer-events-none"
                  style={{ animationDuration: '2.5s' }}
                />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] leading-snug text-[#374151] line-clamp-5">
                  {p.message}
                </p>
              </div>
            </div>

            <div className="flex gap-2 px-4 pb-3">
              <button
                onClick={handleRedeem}
                className="flex-1 bg-[#1E3A5F] hover:bg-[#15304F] text-white text-xs font-semibold rounded-lg px-3 py-2.5 transition-colors shadow-sm"
              >
                {p.proposalType === 'discount'
                  ? `Lo quiero (${p.price < 0 ? `${p.price}€` : `-${Math.abs(p.price)}€`})`
                  : p.proposalType === 'standard' || p.price === 0
                  ? 'Lo quiero'
                  : tNow('home.proposalToastCta', { price: p.price })}
              </button>
              <button
                onClick={handleDismiss}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2.5 transition-colors"
              >
                {tNow('home.proposalToastDismiss')}
              </button>
            </div>
          </div>
        ),
        { id: TOAST_ID, duration: Infinity, position: 'bottom-right' }
      );
    };

    const check = async () => {
      if (stopped) return;
      if (document.visibilityState !== 'visible') return;
      if (inflight) return;
      inflight = true;
      try {
        const r = await fetch(`/api/proposal/anon/${anonId}/active`);
        if (!r.ok) return;
        const d = await r.json();
        if (d?.proposal) showToast(d.proposal as AnonProposal);
      } catch {
        // silent
      } finally {
        inflight = false;
      }
    };

    // Disparar check inmediato al montar
    check();
    // Polling cada 3s
    timer = window.setInterval(check, POLL_INTERVAL_MS);

    // Check inmediato cuando la pestaña vuelve a estar visible o se enfoca
    // — esto cubre el caso "usuario tenía otra pestaña abierta y vuelve".
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onFocus = () => check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [disabled]); // ← solo disabled. onRedeem/t van por ref → no re-arman polling

  return null;
}
